import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, User, CheckCircle, Loader2, Lock } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { SignatureCanvas } from '../components/signature/signatureCanvas';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { documentsClient, requestsClient, unlockWallet } from '../auth/keycloakProvider';
import { isWalletUnlocked } from '../auth/walletSession';
import { DocumentDetailDto, SignDocumentCommand, SignAndSubmitBlockchainRequestCommand } from '../web-api-client';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';

type SignatureRole = 'sender' | 'carrier' | 'consignee';

export default function ECMRSign() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [document, setDocument] = useState<DocumentDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<SignatureRole>('sender');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [signing, setSigning] = useState(false);
  const [walletPassword, setWalletPassword] = useState('');
  const [currentRequestUUID, setCurrentRequestUUID] = useState<string>('');
  const [walletUnlocked, setWalletUnlocked] = useState(false);

  useEffect(() => {
    const loadDocument = async () => {
      if (!id) {
        setError('ID do documento não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const doc = await documentsClient.getDocument(id);
        setDocument(doc);
        setWalletUnlocked(isWalletUnlocked());
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar documento');
        setError(errorToast.description);
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, toast]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh]">
        <LoadingSpinner />
        <p className="text-muted-foreground mt-4">A carregar documento...</p>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh]">
        <p className="text-muted-foreground">{error || 'eCMR não encontrado'}</p>
        <Button variant="link" onClick={() => navigate('/ecmrs')}>
          Voltar aos eCMRs
        </Button>
      </div>
    );
  }

  // Map document to ECMR-like structure for display
  const signatureRoleMap: Record<string, SignatureRole> = {
    'Sender': 'sender',
    'Carrier': 'carrier',
    'Consignee': 'consignee',
  };

  const signatures: { role: SignatureRole; signed: boolean }[] = [
    { role: 'sender', signed: false },
    { role: 'carrier', signed: false },
    { role: 'consignee', signed: false },
  ];

  if (document.signatures && Array.isArray(document.signatures)) {
    document.signatures.forEach((sig: any) => {
      const role = signatureRoleMap[sig.role];
      if (role) {
        const idx = signatures.findIndex(s => s.role === role);
        if (idx !== -1) signatures[idx].signed = true;
      }
    });
  }

  const ecmr = {
    id: document.documentUUID || document.id?.toString() || '',
    number: document.documentNumber || 'N/A',
    senderName: document.senderName || 'N/A',
    companyName: document.companyName || 'N/A',
    consigneeName: document.receiverName || 'N/A',
    goodsDescription: document.cargoDescription || '',
    signatures,
  };

  const roleLabels: Record<SignatureRole, string> = {
    sender: 'Remetente',
    carrier: 'Transportador',
    consignee: 'Destinatário',
  };

  const roleNames: Record<SignatureRole, string> = {
    sender: ecmr.senderName,
    carrier: ecmr.companyName,
    consignee: ecmr.consigneeName,
  };

  const handleUnlockWallet = async () => {
    if (!walletPassword) {
      setError('Por favor, introduza a password da wallet');
      return;
    }

    setUnlocking(true);
    setError(null);

    try {
      await unlockWallet(walletPassword, 60); // 60 minutes session
      setWalletUnlocked(true);
      setShowUnlockModal(false);
      setWalletPassword('');
      toast({
        title: 'Sucesso',
        description: 'Wallet desbloqueada com sucesso! Pode agora assinar documentos.',
      });
      
      // Small delay to ensure session is fully stored and available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After unlocking, proceed with signing
      await performSign();
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao desbloquear wallet');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setUnlocking(false);
    }
  };

  const performSign = async () => {
    if (!id || !signatureData || !document) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Map SignatureRole string to enum value
      const roleMap: Record<SignatureRole, number> = {
        sender: 0,    // SignatureRole.Sender
        carrier: 1,   // SignatureRole.Carrier
        consignee: 2  // SignatureRole.Consignee
      };

      const command = new SignDocumentCommand({
        documentUUID: id,
        signature: signatureData, // Send base64 signature directly
        role: roleMap[selectedRole],
        acceptingParty: selectedRole === 'sender' ? 'Sender' : selectedRole === 'carrier' ? 'Carrier' : 'Consignee',
        signedByName: selectedRole === 'sender' ? document.senderName : selectedRole === 'carrier' ? document.companyName : document.receiverName,
        signatureLocation: document.destinationAddress || '',
        notes: undefined,
      });

      const response = await documentsClient.signDocument(id, command);
      
      // Store the request UUID for blockchain signing
      if (response.requestUUID) {
        setCurrentRequestUUID(response.requestUUID);
        setShowSigningModal(true);
      } else {
        // If no requestUUID, the document was signed without blockchain signing
        toast({
          title: 'Sucesso',
          description: 'eCMR assinado com sucesso!',
        });
        setTimeout(() => {
          navigate(`/ecmrs/${id}`);
        }, 2000);
      }
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar documento');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSign = async () => {
    if (!signatureData) {
      toast({
        title: 'Erro',
        description: 'Por favor, desenhe a sua assinatura',
        variant: 'destructive',
      });
      return;
    }

    // Check if wallet is unlocked (backend wallet)
    if (!isWalletUnlocked()) {
      setShowUnlockModal(true);
      return;
    }

    // Wallet is unlocked, proceed with signing
    await performSign();
  };

  const handleSignAndSubmit = async () => {
    if (!currentRequestUUID) return;

    setSigning(true);
    setError(null);

    try {
      await requestsClient.signAndSubmitBlockchainRequest(new SignAndSubmitBlockchainRequestCommand({
        requestUUID: currentRequestUUID
      }));
      toast({
        title: 'Sucesso',
        description: 'eCMR assinado e registado na blockchain com sucesso!',
      });
      setShowSigningModal(false);
      setTimeout(() => {
        navigate(`/ecmrs/${id}`);
      }, 2000);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar e submeter transação blockchain');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setSigning(false);
    }
  };

  const handleCancelSigning = () => {
    setShowSigningModal(false);
    setCurrentRequestUUID('');
    setError('Assinatura cancelada. O documento foi assinado mas não foi registado na blockchain.');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assinar eCMR"
        description={`Documento: ${ecmr.number}`}
        backLink={`/ecmrs/${id}`}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Wallet Status Banner */}
          <Card className={walletUnlocked ? 'border-green-500/50 bg-green-500/5' : 'border-yellow-500/50 bg-yellow-500/5'}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{walletUnlocked ? '🔓 Wallet Desbloqueada' : '🔒 Wallet Bloqueada'}</p>
                  <p className="text-sm text-muted-foreground">
                    {walletUnlocked 
                      ? 'Pronta para assinar documentos na blockchain'
                      : 'Desbloqueie a wallet para assinar documentos na blockchain'}
                  </p>
                </div>
                {!walletUnlocked && (
                  <Button variant="outline" size="sm" onClick={() => setShowUnlockModal(true)}>
                    Desbloquear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tipo de Assinatura</CardTitle>
              <CardDescription>
                Selecione o seu papel neste transporte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={selectedRole}
                onValueChange={(v) => setSelectedRole(v as SignatureRole)}
                className="grid gap-4 sm:grid-cols-3"
              >
                {(['sender', 'carrier', 'consignee'] as SignatureRole[]).map((role) => {
                  const isAlreadySigned = ecmr.signatures.find(
                    (s) => s.role === role && s.signed
                  );
                  return (
                    <Label
                      key={role}
                      htmlFor={role}
                      className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedRole === role
                          ? 'border-accent bg-accent/5'
                          : 'border-border hover:border-accent/50'
                      } ${isAlreadySigned ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <RadioGroupItem value={role} id={role} className="sr-only" />
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full ${
                          isAlreadySigned ? 'bg-accent/20' : 'bg-muted'
                        }`}
                      >
                        {isAlreadySigned ? (
                          <CheckCircle className="h-6 w-6 text-accent" />
                        ) : (
                          <User className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{roleLabels[role]}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {roleNames[role]}
                        </p>
                        {isAlreadySigned && (
                          <p className="text-xs text-accent mt-1">Já assinado</p>
                        )}
                      </div>
                    </Label>
                  );
                })}
              </RadioGroup>
            </CardContent>
          </Card>

          {ecmr.signatures.every(s => s.signed) ? (
            <Card className="border-green-500/50 bg-green-500/5">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-3 py-4">
                  <CheckCircle className="h-12 w-12 text-accent" />
                  <p className="text-lg font-semibold">Documento Totalmente Assinado</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Todos os intervenientes já assinaram este documento. Não são necessárias mais assinaturas.
                  </p>
                  <Button variant="outline" onClick={() => navigate(`/ecmrs/${id}`)} className="mt-2">
                    Voltar ao Documento
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Desenhar Assinatura</CardTitle>
                  <CardDescription>
                    Use o rato ou o dedo para desenhar a sua assinatura
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SignatureCanvas onSignatureChange={setSignatureData} />
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate(`/ecmrs/${id}`)} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={!signatureData || isSubmitting || !walletUnlocked}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      A assinar...
                    </>
                  ) : !walletUnlocked ? (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Desbloqueie a wallet
                    </>
                  ) : (
                    'Assinar eCMR'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Resumo do Documento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="text-muted-foreground">Número</p>
                <p className="font-medium">{ecmr.number}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Remetente</p>
                <p className="font-medium">{ecmr.senderName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Destinatário</p>
                <p className="font-medium">{ecmr.consigneeName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Mercadorias</p>
                <p className="font-medium">{ecmr.goodsDescription}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estado das Assinaturas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {ecmr.signatures.map((sig) => (
                <div
                  key={sig.role}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                >
                  <span className="text-sm">{roleLabels[sig.role as SignatureRole]}</span>
                  {sig.signed ? (
                    <span className="flex items-center gap-1 text-xs text-accent">
                      <CheckCircle className="h-3 w-3" />
                      Assinado
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Pendente</span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Wallet Unlock Modal */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔐 Desbloquear Wallet</DialogTitle>
            <DialogDescription>
              Introduza a password da wallet para desbloquear para operações blockchain.
              A wallet permanecerá desbloqueada durante 60 minutos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walletPassword">Password da Wallet</Label>
              <Input
                id="walletPassword"
                type="password"
                value={walletPassword}
                onChange={(e) => setWalletPassword(e.target.value)}
                placeholder="Introduza a password"
                onKeyPress={(e) => e.key === 'Enter' && handleUnlockWallet()}
              />
            </div>
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUnlockModal(false);
              setError(null);
              setWalletPassword('');
            }} disabled={unlocking}>
              Cancelar
            </Button>
            <Button onClick={handleUnlockWallet} disabled={unlocking || !walletPassword}>
              {unlocking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A desbloquear...
                </>
              ) : (
                'Desbloquear Wallet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signing Modal */}
      <Dialog open={showSigningModal} onOpenChange={setShowSigningModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Completar Registo na Blockchain</DialogTitle>
            <DialogDescription>
              O seu eCMR foi assinado e está pronto para ser registado na blockchain.
              Clique no botão abaixo para assinar e submeter a transação blockchain.
            </DialogDescription>
          </DialogHeader>
          <div className="py-8">
            {signing ? (
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-center text-muted-foreground">
                  A assinar e submeter para a blockchain...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="text-5xl mb-4">✍️</div>
                <p className="text-center text-sm text-muted-foreground">
                  Request UUID: {currentRequestUUID}
                </p>
              </div>
            )}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelSigning} disabled={signing}>
              Cancelar
            </Button>
            <Button onClick={handleSignAndSubmit} disabled={signing}>
              {signing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A assinar...
                </>
              ) : (
                'Assinar e Submeter para Blockchain'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
